// "Sold by weight" event products (e.g. gelato sold in 140 g cups but supplied and counted by kg).
//
// When an event product has `portionGrams`, EVERY quantity stored for it (starting inventory, the
// daily start/restock/end counts, disposals) is a whole number of GRAMS. People type and read KG,
// so these helpers convert at the edges. `salePricePerUnit` is the price per PORTION and
// `costPerUnit` is the cost per KG. Products without `portionGrams` count plain units, exactly as
// before, and every helper here passes their numbers through unchanged.

export const isWeight = (ep) => !!ep?.portionGrams;

// "cup" → "cups"; falls back to "portion(s)"
export const portionWord = (ep, count = 2) => {
  const w = (ep?.portionLabel || 'portion').trim();
  if (count === 1) return w;
  return /s$/i.test(w) ? w : `${w}s`;
};

// grams (int) ⇄ kg string for form inputs
export const gToKgStr = (g) => (g == null || g === '' ? '' : String(Math.round(Number(g)) / 1000));
export const kgToG = (kg) => {
  if (kg == null || kg === '') return null;
  const n = parseFloat(kg);
  return Number.isNaN(n) ? null : Math.round(n * 1000);
};

export const fmtKg = (g, decimals = 2) => `${(Number(g) / 1000).toFixed(decimals)} kg`;

// Whole-portion counts as integers, otherwise 1 decimal ("27.1")
export const fmtPortions = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

// Number of portions represented by a raw stored quantity (grams for weight products).
export const portions = (ep, qty) => (isWeight(ep) ? Number(qty) / ep.portionGrams : Number(qty));

// A single stored quantity for display: "3.80 kg" for weight products, the plain number otherwise.
export const fmtQty = (ep, qty) => {
  if (qty == null || qty === '') return '—';
  return isWeight(ep) ? fmtKg(qty) : String(qty);
};

// Sold quantity for display: "3.80 kg · 27.1 cups" for weight products, the plain number otherwise.
export const fmtSold = (ep, qty) => {
  if (qty == null) return '—';
  if (!isWeight(ep)) return String(qty);
  const p = portions(ep, qty);
  return `${fmtKg(qty)} · ${fmtPortions(p)} ${portionWord(ep, p === 1 ? 1 : 2)}`;
};

// Cost of one PORTION (costPerUnit is per kg for weight products)
export const costPerPortion = (ep) => {
  const c = parseFloat(ep?.costPerUnit) || 0;
  return isWeight(ep) ? (c * ep.portionGrams) / 1000 : c;
};
